import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUserStore } from "@/store/user.store";
import {
  AlertTriangle,
  GemIcon,
  LifeBuoy,
  LogOut,
  Check,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { bulkSetVertexForAllUsers } from "@/services/api/user.service";
import { Skeleton } from "@/components/ui/skeleton";
import { CreditIcon } from "../ui/custom-icon";
import QueueProgress from "./QueueProgress";
import Link from "next/link";
import { signOut } from "firebase/auth";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { auth } from "@/config/firebase.config";
import { useTeams } from "@/hooks/useTeams";
import useUsers from "@/hooks/useUsers";
import { useBrandStore } from "@/store/brand.store";
import { useEffect, useState } from "react";
import { useStreamContext } from "@/providers/langgraph/Stream";
import { updateActiveTeamIdinThread } from "@/services/api/langgraph.service";
import { useSessionStorage } from "@/hooks/useSessionStorage";

export function UserProfileMenu({}) {
  const {
    user,
    credits,
    kittykatExpertCredits,
    setIsSwitchingTeam,
    isSwitchingTeam,
  } = useUserStore();
  const { myTeamsQuery } = useTeams();

  const { updateActiveTeamAsync } = useUsers();
  const router = useRouter();
  const queryClient = useQueryClient();
  const setUser = useUserStore((s) => s.setUser);
  const { setIsBrandsFetched } = useBrandStore();
  const { values } = useStreamContext();
  const { clearSessionStorage } = useSessionStorage();

  // Bulk Vertex/Studio switch — only offered to this account; the backend
  // enforces the same allowlist server-side.
  const canBulkSwitchVertex = user?.email === "sheldon.kerr@baseux.com";
  const [bulkVertexAction, setBulkVertexAction] = useState<
    "vertex" | "studio" | null
  >(null);

  const handleBulkVertexConfirm = () => {
    if (!bulkVertexAction) return;
    const enabled = bulkVertexAction === "vertex";
    setBulkVertexAction(null);

    toast.promise(bulkSetVertexForAllUsers(enabled), {
      loading: enabled
        ? "Switching all users to Vertex..."
        : "Switching all users to Studio...",
      success: (result) => {
        // Keep the current session's indicator in sync immediately
        if (user) setUser({ ...user, use_vertexai: enabled });
        queryClient.invalidateQueries({ queryKey: ["models"], exact: false });
        return `${result.modified_count} user${
          result.modified_count === 1 ? "" : "s"
        } switched to ${enabled ? "Vertex" : "Studio"}.`;
      },
      error: "Could not update users. Please try again.",
    });
  };

  async function handleLogout() {
    clearSessionStorage();
    await signOut(auth);
    await fetch("/api/logout");
    // reload the page to ensure the user is logged out
    window.location.reload();
    router.push("/login");
  }

  const getUserInitials = () => {
    if (!user?.name) return "U";
    const nameParts = user.name.split(" ");
    if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
    return (
      nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)
    ).toUpperCase();
  };

  //check if thread has actvive team id , if there leave it else update it
  useEffect(() => {
    const updateThreadActiveTeam = async () => {
      // only update the langgraph thread state when we have an active team and a valid thread id
      if (user?.active_team_id && user?.thread_id) {
        if (!values?.activeTeamId && values != null) {
          try {
            await updateActiveTeamIdinThread(
              user.thread_id,
              user.active_team_id
            );
          } catch (err) {
            console.error("Failed to update thread active team id", err);
          }
        }
      }
    };

    updateThreadActiveTeam();
  }, [user?.active_team_id, user?.thread_id, values?.activeTeamId]);

  return (
    <div className="flex items-center justify-center space-x-2 sm:space-x-4 lg:space-x-6">
      {kittykatExpertCredits !== null && (
        <div className="flex items-center gap-1 bg-primary/10 text-primary px-3 py-2 rounded-full h-10 cursor-pointer">
          {isSwitchingTeam ? (
            <Skeleton className="h-4 w-8" />
          ) : (
            <span className="text-xs">
              {kittykatExpertCredits.toLocaleString()}
            </span>
          )}

          <GemIcon className="w-6 h-4" />
        </div>
      )}
      {credits !== null && (
        <div className="flex items-center gap-2 bg-primary/10 text-primary px-3 py-2 rounded-full h-10 cursor-pointer">
          {isSwitchingTeam ? (
            <Skeleton className="h-4 w-8" />
          ) : (
            <span className="text-xs">{credits.toLocaleString()}</span>
          )}
          <CreditIcon className="w-2 h-2" />
        </div>
      )}
      <div className="hidden sm:block">
        <QueueProgress />
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center rounded-full bg-primary/20 cursor-pointer">
          <Avatar className="h-10 w-10 rounded-l-full">
            <AvatarImage alt={user?.name || "User"} />
            <AvatarFallback className="bg-purple-100 text-purple-600">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-72 rounded-lg"
          side="bottom"
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage alt={user?.name} />
                <AvatarFallback className="bg-purple-100 text-purple-600">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user!.name}</span>
                <span className="truncate text-xs">{user!.email}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          <div className="px-1 py-2">
            <div className="text-xs font-semibold text-muted-foreground mb-2">
              Workspaces
            </div>

            {/* Team workspaces only - Individual workspace removed: always use team flow */}

            {/* Teams list */}
            <div className="mt-2 space-y-1">
              {myTeamsQuery.isLoading && (
                <>
                  {/* Render a few skeleton placeholders that match the team item layout */}
                  {[0, 1].map((i) => (
                    <DropdownMenuItem asChild key={`skeleton-${i}`}>
                      <button
                        disabled
                        className={`flex items-center justify-between gap-2 p-2 rounded-md w-full text-left hover:bg-accent/30 pointer-events-none bg-transparent`}
                        aria-hidden
                      >
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-6 w-6 rounded-sm" />
                          <Skeleton className="h-4 w-28 rounded" />
                        </div>
                        <Skeleton className="h-4 w-4 rounded" />
                      </button>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              {myTeamsQuery.data?.teams?.map((team) => (
                <DropdownMenuItem asChild key={team.id}>
                  <button
                    className={`flex items-center justify-between gap-2 p-2 rounded-md w-full text-left hover:bg-accent/30 ${
                      user?.active_team_id === team.id ? "bg-accent/20" : ""
                    }`}
                    onClick={async () => {
                      if (!user) return;
                      setIsSwitchingTeam(true);
                      setIsBrandsFetched(false);
                      try {
                        await updateActiveTeamAsync({
                          userId: user.id,
                          active_team_id: team.id,
                        });
                        // update the activeTeamId in the langgraph thread state
                        // this must use the *thread* id (user.thread_id), not the user's id
                        if (user.thread_id) {
                          await updateActiveTeamIdinThread(
                            user.thread_id,
                            team.id
                          );
                        }

                        setUser({ ...user, active_team_id: team.id });
                      } catch (err) {
                        console.error("Failed to update active team", err);
                      } finally {
                        setIsSwitchingTeam(false);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-6 w-6 rounded-sm bg-primary/20 flex items-center justify-center text-xs text-primary">
                        {team.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="text-sm truncate">{team.name}</div>
                    </div>
                    {user?.active_team_id === team.id && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </button>
                </DropdownMenuItem>
              ))}
            </div>
          </div>
          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link href={"/help"}>
                <LifeBuoy />
                {user?.use_vertexai ? "Help [vai]" : "Help"}
              </Link>
            </DropdownMenuItem>
            {user?.is_default_admin === true && (
              <DropdownMenuItem asChild>
                <Link href={"/issues"}>
                  <AlertTriangle />
                  Issues Report
                </Link>
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          {canBulkSwitchVertex && (
            <>
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onClick={() => setBulkVertexAction("vertex")}
                >
                  <Sparkles />
                  All users to Vertex
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setBulkVertexAction("studio")}
                >
                  <Sparkles />
                  All users to Studio
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={handleLogout}>
            <LogOut />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={bulkVertexAction !== null}
        onOpenChange={(open) => {
          if (!open) setBulkVertexAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkVertexAction === "vertex"
                ? "Switch all users to Vertex?"
                : "Switch all users to Studio?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkVertexAction === "vertex"
                ? "Every user profile will have Use Vertex turned on, routing their Gemini generations through Vertex AI (service account)."
                : "Every user profile will have Use Vertex turned off, routing their Gemini generations through the Gemini Developer API (AI Studio)."}{" "}
              You can still change individual users afterwards in Edit User.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkVertexConfirm}>
              {bulkVertexAction === "vertex"
                ? "Switch all to Vertex"
                : "Switch all to Studio"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
